import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import Select from "react-select";
import { DatePicker } from "antd";
import moment from "moment";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate } from "react-router-dom";

import { countriesData } from "../../../components/Data/countries_v1";
import { states_data } from "../../../components/Data/states";

import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";

import {
  FaCalendarAlt,
  FaUser,
  FaVenusMars,
  FaUsers,
  FaBirthdayCake,
  FaHome,
  FaSignature,
  FaChurch,
  FaRing,
} from "react-icons/fa";

// Add custom parse format plugin
dayjs.extend(customParseFormat);

const UpdateMarriageChild = ({ marriageRecord }) => {
  let navigate = useNavigate();
  const inputWidth = "100%";
  const style = {
    control: (base) => ({
      ...base,
      border: "1px solid #e2e8f0",
      borderRadius: "0.375rem",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid #4299e1",
      },
    }),
  };

  const [{ errorLogApi, placeHolderText, user }] = useAtom(gbState);
  const [selectedGroomSignature, setSelectedGroomSignature] = useState(null);
  const [selectedGroomWitnessSignature, setSelectedGroomWitnessSignature] =
    useState(null);
  const [selectedBrideSignature, setSelectedBrideSignature] = useState(null);
  const [selectedBrideWitnessSignature, setSelectedBrideWitnessSignature] =
    useState(null);
  const [formData, setFormData] = useState({ ...marriageRecord });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  const DATE_FORMAT_DISPLAY = "MM-DD-YYYY";
  const DATE_FORMAT_INTERNAL = "YYYY-MM-DD";

  // Initialize countries on component mount
  useEffect(() => {
    const formattedCountries = countriesData.map((c) => ({
      value: c.id,
      label: c.name,
    }));
    setCountries(formattedCountries);
  }, []);

  const fetchCities = useCallback(async (stateId) => {
    setIsLoadingCities(true);
    try {
      const userToken = localStorage.getItem("token");
      console.log("Fetching cities for stateId:", stateId);
      console.log(
        "API URL:",
        `${process.env.REACT_APP_API_URL}/city/${stateId}`
      );

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/city/${stateId}`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Cities API Response:", response.data);

      if (response.data && response.data.cities) {
        const formattedCities = response.data.cities.map((city) => ({
          value: city.id,
          label: city.name,
        }));
        console.log("Formatted cities:", formattedCities);
        return formattedCities;
      } else {
        console.error("Invalid cities data format:", response.data);
        return [];
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      console.error("Error details:", error.response?.data);
      return [];
    } finally {
      setIsLoadingCities(false);
    }
  }, []);

  // Update states when country changes
  useEffect(() => {
    if (formData.groom_country) {
      const filterStates = states_data
        .filter((s) => s.country_id === formData.groom_country.value)
        .map((s) => ({ value: s.id, label: s.name }));
      setStates(filterStates);
    } else {
      setStates([]);
    }
  }, [formData.groom_country]);

  useEffect(() => {
    if (formData.bride_country) {
      const filterStates = states_data
        .filter((s) => s.country_id === formData.bride_country.value)
        .map((s) => ({ value: s.id, label: s.name }));
      setStates(filterStates);
    } else {
      setStates([]);
    }
  }, [formData.bride_country]);

  // Update cities when state changes
  useEffect(() => {
    const getCities = async () => {
      if (formData.groom_state && formData.groom_state.value) {
        console.log(
          "State changed, fetching cities for:",
          formData.groom_state
        );
        const citiesData = await fetchCities(formData.groom_state.value);
        console.log("Received cities data:", citiesData);
        setCities(citiesData);
      } else {
        setCities([]);
      }
    };
    getCities();
  }, [formData.groom_state, fetchCities]);

  useEffect(() => {
    const getCities = async () => {
      if (formData.bride_state && formData.bride_state.value) {
        console.log(
          "State changed, fetching cities for:",
          formData.bride_state
        );
        const citiesData = await fetchCities(formData.bride_state.value);
        console.log("Received cities data:", citiesData);
        setCities(citiesData);
      } else {
        setCities([]);
      }
    };
    getCities();
  }, [formData.bride_state, fetchCities]);

  const casteListOptions = [
    { value: 1, label: "Assemblies of God in India" },
    { value: 2, label: "Baptist" },
    { value: 3, label: "Chaldean Syrian Church" },
    { value: 4, label: "Church of North India" },
    { value: 5, label: "Church of South India" },
    { value: 6, label: "India Pentecostal Church of God" },
    { value: 7, label: "Jacobite Syrian Orthodox" },
    { value: 8, label: "Lutheran" },
    { value: 9, label: "Malankara Mar Thoma Syrian" },
    { value: 10, label: "Malankara Orthodox Syrian" },
    { value: 11, label: "Methodist Church in India" },
    { value: 12, label: "Other Christian" },
    { value: 13, label: "Roman Catholic" },
    { value: 14, label: "Syro-Malabar Catholic" },
    { value: 15, label: "Syro-Malankara Catholic" },
    { value: 16, label: "The Pentecostal Mission" },
  ];

  const handleSelectChange = (selected, field) => {
    console.log(`${field} selected:`, selected);

    // Handle groom fields
    if (field === "groom_country") {
      setFormData((prev) => ({
        ...prev,
        groom_country: selected,
        groom_state: null,
        groom_city: null,
      }));
    } else if (field === "groom_state") {
      setFormData((prev) => ({
        ...prev,
        groom_state: selected,
        groom_city: null,
      }));
    } else if (field === "groom_city") {
      setFormData((prev) => ({
        ...prev,
        groom_city: selected,
      }));
    }

    // Handle bride fields
    else if (field === "bride_country") {
      setFormData((prev) => ({
        ...prev,
        bride_country: selected,
        bride_state: null,
        bride_city: null,
      }));
    } else if (field === "bride_state") {
      setFormData((prev) => ({
        ...prev,
        bride_state: selected,
        bride_city: null,
      }));
    } else if (field === "bride_city") {
      setFormData((prev) => ({
        ...prev,
        bride_city: selected,
      }));
    }
  };

  const statusOptions = [
    { value: 1, label: "Bachelor" },
    { value: 2, label: "Spinster" },
    { value: 3, label: "Divorcee" },
  ];

  const bannsLicenseOptions = [
    { value: 1, label: "Banns" },
    { value: 2, label: "License" },
  ];

  const handleChange = ({ currentTarget: input }) => {
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  const dropDownChange = (selected, dropdown) => {
    setFormData({
      ...formData,
      [dropdown.name]: selected,
    });
  };

  const handleMarriageRegister = () => {
    let userToken = localStorage.getItem("token");
    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/marriage/update/${marriageRecord?._id}`,
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${userToken}`,
      },
      data: {
        ...formData,
        groom_signature: selectedGroomSignature,
        groom_signature_witness: selectedGroomWitnessSignature,
        bride_signature: selectedBrideSignature,
        bride_signature_witness: selectedBrideWitnessSignature,
      },
    };
    axios(config)
      .then(function (response) {
        toast.success(response?.data?.message);
        navigate("/marriages/list");
      })
      .catch(function (error) {
        toast.error(JSON.stringify(error.response.data));
        errorLogApi(error, "UpdateMarriage", "/marriage/update");
      });
  };

  return (
    <div className="max-w-6xl p-6 mx-auto bg-white rounded-lg shadow-lg">
      <h2 className="mb-6 text-2xl font-semibold text-gray-800">
        Update Marriage Record
      </h2>
      <div className="flex flex-col md:flex-row">
        <fieldset className="w-full p-4 mb-4 border border-gray-300 rounded-md md:mr-2 md:mb-0">
          <legend className="px-2 text-lg font-medium text-gray-700">
            Groom Details
          </legend>
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Marriage date
                </label>
                <DatePicker
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  name="marriage_date"
                  onChange={(date, dateString) => {
                    if (date && date.isValid()) {
                      setFormData({
                        ...formData,
                        marriage_date: date.format(DATE_FORMAT_INTERNAL),
                      });
                    } else {
                      setFormData({
                        ...formData,
                        marriage_date: "",
                      });
                    }
                  }}
                  disabledDate={(current) => {
                    let customDate = dayjs().format(DATE_FORMAT_INTERNAL);
                    return (
                      current &&
                      current > dayjs(customDate, DATE_FORMAT_INTERNAL)
                    );
                  }}
                  showToday={true}
                  value={
                    formData?.marriage_date
                      ? dayjs(formData.marriage_date, DATE_FORMAT_INTERNAL)
                      : null
                  }
                  format={DATE_FORMAT_DISPLAY}
                  suffixIcon={<FaCalendarAlt className="text-gray-400" />}
                />
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Groom christian name
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="groom_christian_name"
                    type="text"
                    required
                    placeholder="Groom christian name"
                    onChange={handleChange}
                    value={formData.groom_christian_name}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaUser className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Groom Surname
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="groom_surname"
                    type="text"
                    required
                    placeholder="Groom surname"
                    onChange={handleChange}
                    value={formData.groom_surname}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaUser className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Date of Birth
                </label>
                <DatePicker
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  name="groom_date_of_birth"
                  onChange={(date, dateString) => {
                    if (date && date.isValid()) {
                      setFormData({
                        ...formData,
                        groom_date_of_birth: date.format(DATE_FORMAT_INTERNAL),
                      });
                    } else {
                      setFormData({
                        ...formData,
                        groom_date_of_birth: "",
                      });
                    }
                  }}
                  disabledDate={(current) => {
                    let customDate = dayjs().format(DATE_FORMAT_INTERNAL);
                    return (
                      current &&
                      current > dayjs(customDate, DATE_FORMAT_INTERNAL)
                    );
                  }}
                  showToday={true}
                  value={
                    formData?.groom_date_of_birth
                      ? dayjs(
                          formData.groom_date_of_birth,
                          DATE_FORMAT_INTERNAL
                        )
                      : null
                  }
                  format={DATE_FORMAT_DISPLAY}
                  suffixIcon={<FaBirthdayCake className="text-gray-400" />}
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Caste
                </label>
                <Select
                  name="groom_caste"
                  onChange={dropDownChange}
                  options={casteListOptions}
                  className={`h-10 outline-none w-[${inputWidth}]`}
                  placeholder={placeHolderText("Select Caste")}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  styles={style}
                  value={formData?.groom_caste}
                />
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Groom Status
                </label>
                <Select
                  name="groom_status"
                  onChange={dropDownChange}
                  options={statusOptions}
                  className={`h-10 outline-none w-[${inputWidth}]`}
                  placeholder={placeHolderText("Select Status")}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  styles={style}
                  value={formData?.groom_status}
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Residential Address
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="groom_residential_address"
                    type="text"
                    required
                    placeholder="Residential Address"
                    onChange={handleChange}
                    value={formData.groom_residential_address}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaHome className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Parent / Guardian
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="groom_parents_or_guardian"
                    type="text"
                    required
                    placeholder="Parents / Guardian"
                    onChange={handleChange}
                    value={formData.groom_parents_or_guardian}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaUsers className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Groom Signature
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="groom_signature"
                    type="file"
                    required
                    placeholder="Signature"
                    onChange={(event) =>
                      setSelectedGroomSignature(event.target.files[0])
                    }
                    accept="image/gif, image/jpeg, image/png"
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaSignature className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Witness Signature
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="groom_signature_witness"
                    type="file"
                    required
                    placeholder="Signature"
                    onChange={(event) =>
                      setSelectedGroomWitnessSignature(event.target.files[0])
                    }
                    accept="image/gif, image/jpeg, image/png"
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaSignature className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Banns / License
                </label>
                <Select
                  name="groom_by_banns_or_license"
                  onChange={dropDownChange}
                  options={bannsLicenseOptions}
                  className={`h-10 outline-none w-[${inputWidth}]`}
                  placeholder={placeHolderText("Banns / License")}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  styles={style}
                  value={formData?.groom_by_banns_or_license}
                />
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Solemnised By
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="solemnised_by"
                    type="text"
                    required
                    placeholder="Solemnised By"
                    onChange={handleChange}
                    value={formData.solemnised_by}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaChurch className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label
                  className="block mb-1 text-sm font-medium"
                  htmlFor="groom_country"
                >
                  Country
                </label>
                <Select
                  name="groom_country"
                  options={countries}
                  styles={style}
                  placeholder={placeHolderText("Country")}
                  onChange={(selected) =>
                    handleSelectChange(selected, "groom_country")
                  }
                  value={formData.groom_country}
                  className="w-full"
                  menuPosition="fixed"
                  isClearable
                />
              </div>
              <div className="w-full md:w-1/2">
                <label
                  className="block mb-1 text-sm font-medium"
                  htmlFor="groom_state"
                >
                  State
                </label>
                <Select
                  name="groom_state"
                  options={states}
                  styles={style}
                  placeholder={placeHolderText("State")}
                  onChange={(selected) =>
                    handleSelectChange(selected, "groom_state")
                  }
                  value={formData.groom_state}
                  className="w-full"
                  menuPosition="fixed"
                  isDisabled={!formData.groom_country}
                  isClearable
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label
                  className="block mb-1 text-sm font-medium"
                  htmlFor="groom_city"
                >
                  City
                </label>
                <Select
                  name="groom_city"
                  options={cities}
                  styles={style}
                  placeholder={placeHolderText("City")}
                  onChange={(selected) =>
                    handleSelectChange(selected, "groom_city")
                  }
                  value={formData.groom_city}
                  className="w-full"
                  menuPosition="fixed"
                  isDisabled={!formData.groom_state}
                  isClearable
                  isLoading={isLoadingCities}
                />
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset className="w-full p-4 border border-gray-300 rounded-md md:ml-2">
          <legend className="px-2 text-lg font-medium text-gray-700">
            Bride Details
          </legend>
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Banns / License
                </label>
                <Select
                  name="bride_by_banns_or_license"
                  onChange={dropDownChange}
                  options={bannsLicenseOptions}
                  className={`h-10 outline-none w-[${inputWidth}]`}
                  placeholder={placeHolderText("Banns / License")}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  styles={style}
                  value={formData?.bride_by_banns_or_license}
                />
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Bride christian name
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="bride_christian_name"
                    type="text"
                    required
                    placeholder="Bride christian name"
                    onChange={handleChange}
                    value={formData.bride_christian_name}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaUser className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Bride Surname
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="bride_surname"
                    type="text"
                    required
                    placeholder="Bride surname"
                    onChange={handleChange}
                    value={formData.bride_surname}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaUser className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Date of Birth
                </label>
                <DatePicker
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  name="bride_date_of_birth"
                  onChange={(date, dateString) => {
                    if (date && date.isValid()) {
                      setFormData({
                        ...formData,
                        bride_date_of_birth: date.format(DATE_FORMAT_INTERNAL),
                      });
                    } else {
                      setFormData({
                        ...formData,
                        bride_date_of_birth: "",
                      });
                    }
                  }}
                  disabledDate={(current) => {
                    let customDate = dayjs().format(DATE_FORMAT_INTERNAL);
                    return (
                      current &&
                      current > dayjs(customDate, DATE_FORMAT_INTERNAL)
                    );
                  }}
                  showToday={true}
                  value={
                    formData?.bride_date_of_birth
                      ? dayjs(
                          formData.bride_date_of_birth,
                          DATE_FORMAT_INTERNAL
                        )
                      : null
                  }
                  format={DATE_FORMAT_DISPLAY}
                  suffixIcon={<FaBirthdayCake className="text-gray-400" />}
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Caste
                </label>
                <Select
                  name="bride_caste"
                  onChange={dropDownChange}
                  options={casteListOptions}
                  className={`h-10 outline-none w-[${inputWidth}]`}
                  placeholder={placeHolderText("Select Caste")}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  styles={style}
                  value={formData?.bride_caste}
                />
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Bride Status
                </label>
                <Select
                  name="bride_status"
                  onChange={dropDownChange}
                  options={statusOptions}
                  className={`h-10 outline-none w-[${inputWidth}]`}
                  placeholder={placeHolderText("Select Status")}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  styles={style}
                  value={formData?.bride_status}
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Residential Address
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="bride_residential_address"
                    type="text"
                    required
                    placeholder="Residential Address"
                    onChange={handleChange}
                    value={formData.bride_residential_address}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaHome className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Parent / Guardian
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="bride_parents_or_guardian"
                    type="text"
                    required
                    placeholder="Parents / Guardian"
                    onChange={handleChange}
                    value={formData.bride_parents_or_guardian}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaUsers className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Bride Signature
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="bride_signature"
                    type="file"
                    required
                    placeholder="Signature"
                    onChange={(event) =>
                      setSelectedBrideSignature(event.target.files[0])
                    }
                    accept="image/gif, image/jpeg, image/png"
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaSignature className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Witness Signature
                </label>
                <div className="relative">
                  <input
                    className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    name="bride_signature_witness"
                    type="file"
                    required
                    placeholder="Signature"
                    onChange={(event) =>
                      setSelectedBrideWitnessSignature(event.target.files[0])
                    }
                    accept="image/gif, image/jpeg, image/png"
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaSignature className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Marriage Location
                </label>
                <textarea
                  className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  name="marriage_location"
                  type="textarea"
                  row={3}
                  required
                  placeholder="Marriage Location"
                  onChange={handleChange}
                  value={formData.marriage_location}
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label
                  className="block mb-1 text-sm font-medium"
                  htmlFor="bride_country"
                >
                  Country
                </label>
                <Select
                  name="bride_country"
                  options={countries}
                  styles={style}
                  placeholder={placeHolderText("Country")}
                  onChange={(selected) =>
                    handleSelectChange(selected, "bride_country")
                  }
                  value={formData.bride_country}
                  className="w-full"
                  menuPosition="fixed"
                  isClearable
                />
              </div>
              <div className="w-full md:w-1/2">
                <label
                  className="block mb-1 text-sm font-medium"
                  htmlFor="bride_state"
                >
                  State
                </label>
                <Select
                  name="bride_state"
                  options={states}
                  styles={style}
                  placeholder={placeHolderText("State")}
                  onChange={(selected) =>
                    handleSelectChange(selected, "bride_state")
                  }
                  value={formData.bride_state}
                  className="w-full"
                  menuPosition="fixed"
                  isDisabled={!formData.bride_country}
                  isClearable
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <div className="w-full md:w-1/2">
                <label
                  className="block mb-1 text-sm font-medium"
                  htmlFor="bride_city"
                >
                  City
                </label>
                <Select
                  name="bride_city"
                  options={cities}
                  styles={style}
                  placeholder={placeHolderText("City")}
                  onChange={(selected) =>
                    handleSelectChange(selected, "bride_city")
                  }
                  value={formData.bride_city}
                  className="w-full"
                  menuPosition="fixed"
                  isDisabled={!formData.bride_state}
                  isClearable
                  isLoading={isLoadingCities}
                />
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      <div className="flex justify-end mt-8 space-x-4">
        <button
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={handleMarriageRegister}
        >
          Update Record
        </button>
      </div>
    </div>
  );
};

export default UpdateMarriageChild;
